import { inputCharactersLimitValidator } from 'shop/components/Checkout'
import {
  Controller,
  ControllerRenderProps,
  FieldError,
  UseFormReturn
} from 'react-hook-form'
import FormInput from '../Inputs/FormInput'
import { CheckoutForm, FormFields, FORM_DEFAULTS } from './types'
import { useConsumerCart, useMediaQueries } from 'shop/hooks'
import { useEffect, useRef } from 'react'
import { scrollToElement } from '../Controls/utils'
import styled from '@emotion/styled'
import ToggleSwitchSection from '../Forms/ToggleSwitchSection'
import { handleFormFieldCartUpdate } from './formCartUpdates'
import { cloneDeep } from 'lodash'
import { useFocusOutListener } from 'shop/hooks/useFocusOutListener'

type OrderNotesProps = {
  formHandle: UseFormReturn<FormFields>
  placeholder?: string
  error?: FieldError
  prevOrderNotesRef: React.MutableRefObject<
    CheckoutForm['orderNotes'] | undefined
  >
}

export const HeaderWithSwitch = styled.div(
  ({ enabled }: { enabled: boolean }) => ({
    display: 'flex',
    alignItems: 'center',
    marginBottom: enabled ? '16px' : '0'
  })
)

const OrderNotes = ({
  error,
  placeholder = '',
  formHandle,
  prevOrderNotesRef
}: OrderNotesProps) => {
  const { register, watch, control, clearErrors } = formHandle
  const orderNotes = watch('orderNotes')
  const orderNotesEnabled = watch('orderNotesEnabled')
  const formInitialised = watch('__formInitialised')
  const { isMobile } = useMediaQueries()
  const { cart, updateConsumerCart } = useConsumerCart()
  const sectionRef = useRef<HTMLDivElement>(null)
  const isMounted = useRef(false)

  useEffect(() => {
    if (orderNotesEnabled && isMobile && !!isMounted.current) {
      // scroll to section when toggling it on for Mobile
      // timeout for collapsiing section animations
      setTimeout(() => {
        scrollToElement('order-notes-section', 80)
      }, 400)
    }
    isMounted.current = true // stops scroll trigger on page load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNotesEnabled])

  const handleToggle = (
    isChecked: boolean,
    fieldOnChange: ControllerRenderProps['onChange']
  ) => {
    if (!isChecked) {
      clearErrors('orderNotes')
      clearErrors('orderNotesEnabled')
      updateConsumerCart({ orderNotes: null })
      prevOrderNotesRef.current = FORM_DEFAULTS['orderNotes']
    } else {
      if (orderNotes.length > 0 && !cart?.orderNotes) {
        updateConsumerCart({ orderNotes })
        prevOrderNotesRef.current = cloneDeep(orderNotes)
      }
    }
    fieldOnChange(isChecked)
  }

  // Set listener for when the form section loses focus to trigger update cart and collapse.
  const handleFocusOut = async (e: FocusEvent) => {
    handleFormFieldCartUpdate({
      formRef: sectionRef,
      checkoutForm: formHandle,
      event: e,
      prevFieldValue: prevOrderNotesRef,
      formFieldName: 'orderNotes',
      updateConsumerCart
    })
  }
  useFocusOutListener({ sectionRef, onFocusOut: handleFocusOut })

  if (!formInitialised) return <></>

  return (
    <Controller
      name='orderNotesEnabled'
      control={control}
      render={({ field }) => (
        <ToggleSwitchSection
          switchName={field.name}
          label='Add order notes'
          onToggle={(isChecked) => handleToggle(isChecked, field.onChange)}
          defaultChecked={field.value}
        >
          <InputContainer ref={sectionRef}>
            <FormInput
              id={'orderNotes'}
              variant='textarea'
              value={orderNotes}
              data-testid='order-notes-text-area'
              labelText='Order notes'
              formRef={register('orderNotes', {
                required: field.value ? 'Please enter order notes' : false,
                validate: inputCharactersLimitValidator('Order notes')
              })}
              name='orderNotes'
              placeholder={placeholder}
              showError={!!error}
              scrollSettings={{
                scrollOnFocus: true,
                scrollToElementId: 'order-notes-section'
              }}
            />
          </InputContainer>
        </ToggleSwitchSection>
      )}
    />
  )
}

const InputContainer = styled.div(() => ({
  paddingTop: '16px'
}))

export default OrderNotes
